import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useProfileStore } from '@/stores/profile'
import { competitiveEnabled } from '@/lib/featureFlags'
import HomeView from '@/views/HomeView.vue'

const fakeAuthEnabled = (() => {
  const value = import.meta.env.VITE_EUCHRE_FAKE_AUTH
  if (!value) return false
  const normalized = value.trim().toLowerCase()
  return normalized === 'true' || normalized === '1' || normalized === 'yes'
})()

const devLoginEnabled = import.meta.env.DEV || fakeAuthEnabled

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: HomeView,
    meta: { order: 1 }
  },
  {
    path: '/learn',
    name: 'learn',
    component: () => import('@/views/LearnView.vue'),
    meta: { order: 1 }
  },
  // The Learn page replaced the old static tutorial; keep deep links alive.
  {
    path: '/tutorial',
    redirect: { name: 'learn' },
    meta: { order: 1 }
  },
  {
    path: '/about',
    name: 'about',
    component: () => import('@/views/AboutView.vue'),
    meta: { order: 1 }
  },
  {
    path: '/membership',
    name: 'membership',
    component: () => import('@/views/MembershipView.vue'),
    meta: { order: 1 }
  },
  {
    path: '/lobby',
    name: 'lobby',
    component: () => import('@/views/LobbyView.vue'),
    meta: { requiresAuth: true, order: 2 }
  },
  // Legacy deep links — the lobby is now the single hub; these folded into its
  // tabs. Redirect (instead of removing) so old links and specs keep resolving.
  {
    path: '/lobby/active',
    name: 'lobby-active',
    redirect: { name: 'lobby', query: { tab: 'private' } },
    meta: { requiresAuth: true, order: 2 }
  },
  {
    path: '/lobby/public',
    name: 'lobby-public',
    redirect: { name: 'lobby', query: { tab: 'public' } },
    meta: { requiresAuth: true, order: 2 }
  },
  {
    path: '/partner',
    name: 'partner',
    component: () => import('@/views/PartnerLobbyView.vue'),
    meta: { requiresAuth: true, order: 2 }
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('@/views/ProfileView.vue'),
    meta: { requiresAuth: true, order: 2 }
  },
  {
    path: '/game/:id',
    name: 'game',
    component: () => import('@/views/GameView.vue'),
    meta: { requiresAuth: true, order: 3 }
  },
  {
    path: '/game/:gameId/invitation',
    name: 'game-invitation',
    component: () => import('@/views/InvitationView.vue'),
    meta: { order: 3 }
  },
  {
    path: '/scenario-lab',
    name: 'scenario-lab',
    component: () => import('@/views/ScenarioLabView.vue'),
    meta: { requiresAuth: true, order: 4 }
  },
  {
    path: '/dev-testing',
    name: 'dev-testing',
    component: () => import('@/views/DevTestingView.vue'),
    meta: { requiresAuth: true, order: 5 }
  }
]

// Competitive marketing surfaces ship behind VITE_EUCHRE_COMPETITIVE so they can roll out
// dark while Home/About/Pricing default on. Public (no auth) — see 08 §4.
if (competitiveEnabled) {
  routes.push(
    {
      path: '/rankings',
      name: 'rankings',
      component: () => import('@/views/RankingsView.vue'),
      meta: { order: 1 }
    },
    {
      path: '/tournaments',
      name: 'tournaments',
      component: () => import('@/views/TournamentsView.vue'),
      meta: { order: 1 }
    }
  )
}

if (devLoginEnabled) {
  routes.push({
    path: '/dev-login',
    name: 'dev-login',
    component: () => import('@/views/DevLoginView.vue'),
    meta: { order: 0 }
  })
}

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Optional per-route authentication check
router.beforeEach(async (to) => {
  if (to.meta.requiresAuth) {
    const authStore = useAuthStore()
    const profileStore = useProfileStore()

    if (!authStore.isAuthenticated) {
      await authStore.checkSession()
      
      if (!authStore.isAuthenticated) {
        // Special handling for game routes - show invitation instead of redirecting home
        if (to.name === 'game' && to.params.id) {
          return {
            name: 'game-invitation',
            params: { gameId: to.params.id }
          }
        }
        
        // Redirect to home with return URL for other routes
        return {
          name: 'home',
          query: { returnTo: to.fullPath }
        }
      }
    }

    if (!profileStore.isLoaded && !profileStore.isLoading) {
      try {
        await profileStore.fetchProfile()
      } catch (error) {
        console.error('Failed to load profile', error)
      }
    }

    if (!profileStore.hasProfile && to.name !== 'profile') {
      return {
        name: 'profile',
        query: { returnTo: to.fullPath }
      }
    }
  }
})

export default router
